import { useEffect, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronLeft } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { BottomSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import {
  OptionButton,
  OptionsList,
  RadioCircle,
  SheetQuestion,
} from "@acto/taskpane/field-sales-2/components/primitives/SheetOptions";

const REASONS = ["seasonality", "projectBusiness", "insolvency", "other"] as const;

export type DismissReason = (typeof REASONS)[number];

const CommentField = styled("textarea")(({ theme }) => ({
  minHeight: "7.5rem",
  marginTop: theme.fieldSalesTwo.spacing[8],
  marginInline: theme.fieldSalesTwo.spacing[16],
  padding: theme.fieldSalesTwo.spacing[20],
  boxSizing: "border-box",
  resize: "none",
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": {
    color: theme.fieldSalesTwo.colors.neutral[700],
  },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
}));

interface DismissCustomerSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: DismissReason, comment?: string) => void;
  isSubmitting?: boolean;
}

/**
 * Feedback step of the dismiss-customer flow: asks why the customer is not relevant
 * (single-select reason plus an optional free-text comment) before confirming.
 */
export function DismissCustomerSheet({ isOpen, onOpenChange, onConfirm, isSubmitting }: DismissCustomerSheetProps) {
  const t = useTranslations("fieldSalesTwo.customerActions");
  const [selectedReason, setSelectedReason] = useState<DismissReason | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedReason(null);
      setComment("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selectedReason || isSubmitting) return;
    const trimmedComment = comment.trim();
    onConfirm(selectedReason, trimmedComment === "" ? undefined : trimmedComment);
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("dismiss.sheetTitle")}
      closeAriaLabel={t("dismiss.backAriaLabel")}
      closeIcon={<ChevronLeft />}
    >
      <SheetQuestion id="dismiss-customer-question">{t("dismiss.question")}</SheetQuestion>
      <OptionsList
        value={selectedReason ?? ""}
        onValueChange={(next) => setSelectedReason(next as DismissReason)}
        aria-labelledby="dismiss-customer-question"
      >
        {REASONS.map((reason) => (
          <OptionButton key={reason} value={reason}>
            {t(`dismiss.reasons.${reason}`)}
            <RadioCircle aria-hidden="true" />
          </OptionButton>
        ))}
      </OptionsList>
      <CommentField
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        placeholder={t("dismiss.commentPlaceholder")}
        aria-label={t("dismiss.commentPlaceholder")}
      />
      <CtaRow>
        <SolidCtaButton type="button" disabled={!selectedReason || isSubmitting} onClick={handleConfirm}>
          {t("dismiss.confirm")}
        </SolidCtaButton>
      </CtaRow>
    </BottomSheet>
  );
}
